import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

interface Column<T> {
  key?: string;
  header: React.ReactNode;
  render?: (item: T) => React.ReactNode;
  accessor?: (item: T) => React.ReactNode;
  className?: string;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  isLoading?: boolean;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
  rowClassName?: (item: T) => string;
  renderExpandedRow?: (item: T) => React.ReactNode;
}

export function Table<T extends { id: string | number }>({
  columns,
  data,
  isLoading,
  emptyMessage = 'No data found',
  onRowClick,
  rowClassName,
  renderExpandedRow,
}: TableProps<T>) {
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)]">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[var(--border-color)]">
          <thead className="bg-[var(--bg-tertiary)]">
            <tr>
              {columns.map((column, idx) => (
                <th
                  key={column.key || `col-${idx}`}
                  className={clsx(
                    'px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider',
                    'text-[var(--text-secondary)]',
                    column.className
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-color)]">
            {isLoading ? (
              // Loading skeleton
              [...Array(5)].map((_, i) => (
                <tr key={i}>
                  {columns.map((_, j) => (
                    <td key={j} className="px-6 py-4">
                      <div className="h-4 bg-[var(--bg-tertiary)] rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-6 py-12 text-center text-[var(--text-muted)]"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <React.Fragment key={item.id}>
                  <motion.tr
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => onRowClick?.(item)}
                    className={clsx(
                      'transition-colors duration-200',
                      'hover:bg-[var(--bg-card-hover)]',
                      onRowClick && 'cursor-pointer',
                      rowClassName?.(item)
                    )}
                  >
                    {columns.map((column, colIdx) => (
                      <td
                        key={column.key || `cell-${colIdx}`}
                        className={clsx(
                          'px-6 py-4 text-sm text-[var(--text-primary)]',
                          column.className
                        )}
                      >
                        {column.render
                          ? column.render(item)
                          : column.accessor
                          ? column.accessor(item)
                          : column.key
                          ? (item as Record<string, unknown>)[column.key] as React.ReactNode
                          : null}
                      </td>
                    ))}
                  </motion.tr>
                  {renderExpandedRow && renderExpandedRow(item)}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Table;
